import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { GifPreviewPlaceholder } from "@/components/UI/Skeleton";
import { IGif, INicheFeedbackResponse, INicheMap, IUserMap } from "@/common/services/types";
import { GifPreview } from "@/components/gifPreview/GifPreview";
import { InfinitePlayerScroll } from "@/components/player-feed/infiniteScroll";
import { mainBus } from "@/common/buses";
import { TAddFeedback } from "@/components/gifPreview/Vote";

interface IProps {
  gifs: IGif[];
  users: IUserMap;
  niches?: INicheMap;
  injector?: (pos: number, gif: IGif) => JSX.Element;
  rel?: string;
  isLoading?: boolean;
  isError?: boolean;
  fetchMore: () => void;
  hasMore: boolean;
  injectPlaceholders?: (pos: number) => JSX.Element;
  addNicheFeedback?: TAddFeedback;
  nicheFeedbacks?: INicheFeedbackResponse[];
}

const PLACEHOLDER_ITEMS_LENGTH = 6;

export const InjectionFeed = (
  {
    gifs,
    users,
    niches,
    injector,
    rel,
    isLoading,
    isError,
    fetchMore,
    hasMore,
    injectPlaceholders,
    addNicheFeedback,
    nicheFeedbacks
  }: IProps
) => {
  const [hiddenGifs, setHiddenGifs] = useState<string[]>([]);
  const [canInject, setCanInject] = useState<boolean>(false);

  const filteredGifs = useMemo(() => {
    return gifs.filter((gif) => !hiddenGifs.includes(gif.id));
  }, [hiddenGifs, gifs]);

  const hideGif = useCallback((id: string) => {
    setHiddenGifs((prev) => [...prev, id]);
  }, []);

  const renderGifs = useMemo(() => {
    if (isError) {
      return <p className="error">Error loading gifs. Please refresh the page</p>;
    }

    if (!filteredGifs.length && isLoading) {
      return new Array(PLACEHOLDER_ITEMS_LENGTH).fill("").map((___, index) => (
        <React.Fragment key={index}>
          <GifPreviewPlaceholder />
        </React.Fragment>
      ));
    }

    if (!filteredGifs.length && !isLoading) {
      return <p className="error">No gifs to show on this page.</p>;
    }

    const items = filteredGifs.map((gif: IGif, index: number) => (
      <React.Fragment key={index}>
        <GifPreview
          key={gif.id}
          gif={gif}
          user={users[gif.userName]}
          niches={niches}
          rel={rel}
          position={index + 1}
          hideGif={hideGif}
          addNicheFeedback={addNicheFeedback}
          nicheFeedbacks={nicheFeedbacks}
        />
        {!canInject && injectPlaceholders && injectPlaceholders(index)}
        {canInject && injector && injector(index, gif)}
      </React.Fragment>
    ));

    if (filteredGifs.length > 1) {
      items.push(<InfinitePlayerScroll key="InfinitePlayerScroll" fetchMore={fetchMore} hasMore={hasMore} />);
    }

    return items;
  }, [filteredGifs, hideGif, injector, isLoading, isError, niches, rel, users, fetchMore, hasMore, canInject, injectPlaceholders]);

  const updateInjection = useCallback(() => {
    setCanInject(prevState => prevState ? prevState : true);
  }, []);

  useEffect(() => {
    mainBus.on("set_playing_gif", updateInjection);

    return () => {
      mainBus.off("set_playing_gif", updateInjection);
    };
  }, [updateInjection]);

  return (<Fragment>{renderGifs}</Fragment>);
};
